import Store from '@/store'
import { inIframe, setLocalStorageAndCookie, getOuterHeight } from '@/utils/helpers'
import { handleDIDRedirectResponse, base64urlencode, handleSHA256Response } from '@/utils/didHelpers'

const sendPostMessage = (data: object, frame: Window | null = null) => {
  if (frame) {
    frame.postMessage(data, '*') // frame origin, basically merchant's site
  } else {
    parent.postMessage(data, getHostUri()) // parent origin, basically merchant's site
  }
}

// We manually set the host origin in the integration script.
// host origin meaning the origin (baseUrl) of the merchant's site
function getHostUri () {
  if (process.env.NODE_ENV === 'test' || Store.state.context.config.hostUri === 'file://') {
    return '*'
  } else {
    return Store.state.context.config.hostUri || '*'
  }
}

const __API__ = (): any => {
  // Listeners to postMessage-events for making callbacks to the host-page
  // Only need to have this enabled if the app is being displayed in iframe
  window.addEventListener('message', (e: MessageEvent) => {
    if (e.data) {
      const message = typeof e.data === 'string' ? JSON.parse(e.data) : e.data
      let hostUri = getHostUri()
      const whiteListedOrigins = [
        'https://demo-api.gii.cloud',
        'https://api.gii.cloud',
        'http://localhost:4000',
        'http://localhost:8080',
        'https://pay.paymentiq.io',
        'null'
      ]
      if (e.origin !== '*' && e.origin !== hostUri && !whiteListedOrigins.includes(e.origin)) {
        if (e.data.eventType) {
          // Some error tracing - we're gonna thank ourselves in the future for this one
          console.error('Failed to read cashier post message - invalid origin', e.origin, e.data.eventType)
        }
        return
      }

      switch (message.eventType) {
        case 'PIQ_DEVICE_ID':
          if (!message.payload.deviceId) {
            console.log(message)
            console.error('PIQ deviceId is missing')
          } else if (localStorage.getItem('piq_dId') === null) {
            // We only want to update the deviceId value if we don't already have one.
            // The deviceId is derived from the browsers settings, number of plugins etc. These parameter may change slightly over time
            // giving the user a new deviceId fingerprint.

            // TODO: Investigate if DID allow updating deviceId for a user. In that case wait until we have an identified user, then verify that the deviceId is the same
            // as the generated one, if not - update it.
            setLocalStorageAndCookie('piq_dId', message.payload.deviceId)
          }
          break
        case 'PIQ_LOOKUP_SHA_256':
          return handleSHA256Response(base64urlencode(message.payload.codeChallenge), message.payload.codeVerifier)
      }

      if (message.action === 'gii::redirect') {
        handleDIDRedirectResponse(message)
      }
      if (message.action === 'gii::location_href') {
        // When DID sends message to open a link or app (bankId for example). We need to trigger that redirect from the outer frame
        // So we send out a postMessage ourselves that gets picked up by the integrationscript
        TOP_URL_REPLACE(message.href)
      }
    }
  }, false)

  const APICallback = {
    customerIdentified: (payload) => {
      sendPostMessage(payload)
    }
  }
}

const __CALLBACK_INIT_LOAD__ = () => {
  sendPostMessage(
    {
      eventType: 'LOOKUP_INIT_LOAD'
    })
}

const TOP_URL_REPLACE = (url: string): any => {
  sendPostMessage({
    eventType: 'TOP_URL_REPLACE',
    payload: {
      url
    }
  })
}

const __GENERATE_SHA_256__ = (codeVerifier: string): any => {
  sendPostMessage({
    eventType: 'PIQ_LOOKUP_SHA_256',
    payload: {
      codeVerifier
    }
  })
}

/*
  This is a critical part of the Checkout
  When this application (Lookup) successfully have identified a user, it will communicate to the integration/bootstrapper
  that it's time to render the cashier (payment section).
  If the cashier is already rendered, the cashier will get unpaused.
  User-data is passed to allow input-fields to get prefilled. Any attributes passed will be appended to every transaction
  made by the cashier

  @userId - unique id for the user - received from source (e.g Devcode identity)
  @user - arbitrary object with user information (passing on nationalid for example - required for making Santander invoice payments)
  @attributes - arbitrary object - pass on any custom info you want appended to the transactions
  @didToken - api token for making requests to DevcodeIdentity
*/

const __CALLBACK_CUSTOMER_IDENTIFIED__ = (userId: string, user: object, attributes: object, didToken: string | undefined): any => {
  // Sanity check, we end up calling customer identified both when getting didData + saving editForm which can cause double cashiers to be rendered
  sendPostMessage({
    eventType: 'PIQ_LOOKUP_CUSTOMER_IDENTIFIED',
    payload: {
      status: 'FROM_SOURCE',
      userId,
      user,
      attributes,
      didToken
    }
  })
}

const __CALLBACK_CUSTOMER_EDIT__ = (): any => {
  sendPostMessage({
    eventType: 'PIQ_LOOKUP_EDIT_USER'
  })
}

const __CALLBACK_CUSTOMER_RESET__ = (): any => {
  sendPostMessage({
    eventType: 'PIQ_LOOKUP_CUSTOMER_RESET'
  })
}

const __REPORT_CLIENT_HEIGHT__ = (added = 0): any => {
  const height = getOuterHeight() + added
  sendPostMessage({
    eventType: 'PIQ_LOOKUP_REPORT_HEIGHT',
    payload: {
      height
    }
  })
}

const __SCROLL_TO_TOP__ = (offset = 0): any => {
  sendPostMessage(
    {
      eventType: 'PIQ_LOOKUP_SCROLL_TO_TOP',
      payload: {
        yPos: offset
      }
    }
  )
}

export default {
  __API__,
  __CALLBACK_INIT_LOAD__,
  __GENERATE_SHA_256__,
  __CALLBACK_CUSTOMER_IDENTIFIED__,
  __CALLBACK_CUSTOMER_RESET__,
  __CALLBACK_CUSTOMER_EDIT__,
  __REPORT_CLIENT_HEIGHT__
}
